import { Switch } from '@/components/ui/switch';
import type { TServices } from '@/services/services';
import { currencyFormat } from '@/utils/utils';
import {
  CalendarDays,
  Check,
  ClipboardIcon,
  CreditCard,
  DollarSign,
  Edit3,
  EditIcon,
  ExternalLinkIcon,
  Eye,
  FileTextIcon,
  CopyIcon,
  PencilIcon,
  Percent,
  PowerCircleIcon,
  Puzzle,
  Users,
  X
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import TooltipRenderer from '../../tooltip/TooltipRenderer';
import { toast } from 'sonner';

const ServiceCard: React.FC<{
  service: TServices;
  onSwitchService: () => void;
  isPendingSwitch: boolean;
  disabledSwitch: boolean;
  onSwitchShowOnlyInLanding: () => void;
}> = ({
  service,
  onSwitchService,
  isPendingSwitch,
  disabledSwitch,
  onSwitchShowOnlyInLanding
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const webAppUrl = process.env.WEB_APP_URL;
  const {
    name,
    price,
    organization,
    reservationPct,
    isExtension,
    visibility,
    allowPayment,
    canUpdateReservations,
    enabled,
    description,
    minConsumers,
    maxConsumers,
    showOnlyInLanding
  } = service;

  const items = [
    {
      title: t('common.price'),
      value: currencyFormat(price, organization.currency),
      icon: DollarSign
    },
    {
      title: t('common.reservePercentage'),
      value: reservationPct,
      icon: Percent
    },
    {
      tooltipText: t('services.visibility'),
      tooltipContent: t('services.visibilityDescription'),
      title: t('common.visibility'),
      value: t(`common.${visibility}`),
      icon: Eye
    },
    {
      title: t('services.minConsumers'),
      value: minConsumers,
      icon: Users
    },
    {
      title: t('services.maxConsumers'),
      value: maxConsumers,
      icon: Users
    },
    {
      title: t('services.minAge'),
      value: service.minAge || t('services.noDefined'),
      icon: CalendarDays
    },
    {
      title: t('services.maxAge'),
      value: service.maxAge || t('services.noDefined'),
      icon: CalendarDays
    },
    {
      tooltipText: t('common.allowPayment'),
      tooltipContent: t('services.allowPaymentDescription'),
      title: t('common.allowPayment'),
      value: t(`common.${allowPayment}`),
      icon: CreditCard
    },
    {
      tooltipText: t('common.canUpdateReservations'),
      tooltipContent: t('services.canUpdateReservationsDescription'),
      title: t('common.canUpdateReservations'),
      value: t(`common.${canUpdateReservations}`),
      icon: Edit3
    },
    {
      title: t('common.isExtension'),
      value: isExtension ? <Check color="#10B981" /> : <X color="#EF4444" />,
      icon: Puzzle
    }
  ];

  return (
    <div className="p-6 rounded-md bg-accent">
      <Link to={`./edit`}>
        <div className="flex items-start justify-between ">
          <h1 className="font-bold text-md text-primary">{name}</h1>
          <EditIcon className="cursor-pointer" />
        </div>
      </Link>
      <p className="py-4">{description}</p>
      <div className="items-baseline grid-cols-2 gap-5 p-6 space-y-4 bg-white rounded-md md:grid auto-rows-auto md:space-y-0 lg:grid-cols-3 lg:items-end ">
        {items.map((item, index) => (
          <div key={`${item.title}-${index}`}>
            <div className="flex items-center">
              {item.icon ? <item.icon className="w-4 h-4 mr-2" /> : null}
              {item.tooltipText ? (
                <TooltipRenderer
                  text={item.tooltipText}
                  content={item.tooltipContent}
                />
              ) : (
                <p className="font-semibold">{item.title}</p>
              )}
            </div>
            <span className="flex ml-7">{item.value}</span>
          </div>
        ))}
      </div>
      <div className="p-6 mt-3 bg-white rounded-md">
        <div className="flex items-center justify-between ">
          <div className="flex items-center">
            <PowerCircleIcon className="w-4 h-4 mr-2" />
            <p className="font-semibold">{t('common.enabled')}</p>
          </div>
          <Switch
            className="ml-2"
            checked={enabled}
            onCheckedChange={onSwitchService}
            disabled={disabledSwitch || isPendingSwitch}
            id="airplane-mode"
          />
        </div>
        <p className="mt-3 text-sm">{t('serviceDetail.enabledDescription')}</p>
      </div>
      <div className="p-6 mt-3 bg-white rounded-md">
        <div className="flex items-center justify-between ">
          <div className="flex items-center">
            <FileTextIcon className="w-4 h-4 mr-2" />
            <p className="font-semibold">
              {t('serviceDetail.showOnlyInLanding')}
            </p>
          </div>
          <div className="flex items-center">
            <PencilIcon
              className="w-5 h-5 mr-2 cursor-pointer hover:text-primary transition-colors duration-150"
              onClick={() => {
                navigate(`./edit-landing`);
              }}
            />
            <Switch
              className="ml-2"
              checked={showOnlyInLanding}
              onCheckedChange={onSwitchShowOnlyInLanding}
              disabled={disabledSwitch || isPendingSwitch}
              id="airplane-mode"
            />
          </div>
        </div>
        <p className="mt-3 text-sm">
          {showOnlyInLanding ? (
            <>
              {t('serviceDetail.allowedShowOnlyInLanding')}
              <div className="flex items-center mt-1">
                <Link
                  to={`${webAppUrl}${organization.code}/${service.id}`}
                  target="_blank"
                  className="text-primary flex items-center underline"
                >
                  <p>{t('serviceDetail.serviceLandingPage')}</p>
                  <ExternalLinkIcon className="w-4 h-4 ml-2" />
                </Link>
                <CopyIcon
                  className="w-4 h-4 ml-2 cursor-pointer text-primary hover:text-primary/80 transition-colors duration-150"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `${webAppUrl}${organization.code}/${service.id}`
                    );
                    toast.success(t('serviceDetail.copiedToClipboard'));
                  }}
                />
              </div>
            </>
          ) : (
            t('serviceDetail.showOnlyInLandingDescription')
          )}
        </p>
      </div>
    </div>
  );
};

export default ServiceCard;
